/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import type { Locale } from '@/types/i18n';
import type { ShelfPart } from '@/types/legacy';
import { getBaseSchemaUrl } from '@/utils/common';
import type { SchemaOffers } from '@/types/seoSchema';
import { currencyLocales } from '@/constants/locale';

type ShelfProductSchema = {
  '@type': string;
  name: string;
  image: string;
  url: string;
  sku: number;
  description: string;
  mpn: string;
  brand: { '@type': 'Brand'; name: string };
  offers?: SchemaOffers;
};

export type ShelfSchema = {
  '@context': string;
  '@type': string;
  name: string;
  url: string;
  mainEntity: {
    '@type': string;
    offers: {
      '@type': string;
      itemOffered: ShelfProductSchema[];
    };
  };
};

export const generateShelfSchema = ({
  parts,
  asPath,
  locale,
  shelfCatLabel,
}: {
  parts: ShelfPart[];
  asPath: string;
  locale: Locale;
  shelfCatLabel?: string;
}): ShelfSchema => {
  const baseUrl = getBaseSchemaUrl(locale);

  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: shelfCatLabel || '',
    url: `${baseUrl}${asPath}`,
    mainEntity: {
      '@type': 'WebPageElement',
      offers: {
        '@type': 'Offer',
        itemOffered: parts.map((part) => {
          const {
            name,
            productImageUrl: image,
            seoUrl,
            brand,
            skuNumber: sku,
            partNumber,
            description,
          } = part;

          const productSchema: ShelfProductSchema = {
            '@type': 'Product',
            name,
            image,
            description,
            mpn: partNumber,
            url: `${baseUrl}${seoUrl}`,
            brand: { '@type': 'Brand', name: brand },
            sku,
          };
          const retailPrice = part.skuPricingAndAvailability?.retailPrice || 0;
          const corePrice = part.skuPricingAndAvailability?.corePrice || 0;
          const price = (retailPrice + corePrice).toFixed(2);
          const discountedPrice = part.skuPricingAndAvailability?.discountedPrice;

          const isProductAvailable =
            part.skuPricingAndAvailability?.skuAvailabilityInfo?.fulfillmentOptions?.some(
              (option) => option.availableQty > 0
            );

          productSchema.offers = {
            '@type': 'Offer',
            url: `${baseUrl}${seoUrl}`,
            availability: isProductAvailable
              ? 'https://schema.org/InStock'
              : 'https://schema.org/OutOfStock',
            price: discountedPrice || price,
            priceSpecification: [
              {
                '@type': 'UnitPriceSpecification',
                price,
                priceCurrency: currencyLocales[locale],
                priceType: discountedPrice
                  ? 'https://schema.org/StrikethroughPrice'
                  : 'https://schema.org/ListPrice',
              },
            ],
            priceCurrency: currencyLocales[locale],
            hasMerchantReturnPolicy: {
              '@type': 'MerchantReturnPolicy',
              returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
              returnPolicyDays: 30,
              returnMethod: 'https://schema.org/ReturnByMail',
              returnFees: 'https://schema.org/FreeReturn',
            },
          };

          return productSchema;
        }),
      },
    },
  };
};
